import { Container, Graphics } from "pixi.js"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { FLORA_TILESET, FLOWER_PATCH_TILES, TRANSPARENT_SMALL_FLOWER_TILES } from "game-common/room/flora"
import { TileSize } from "game-common/models"
import { CLASSIC_GRASS_TILE, DAPPLED_GRASS_START, FOREST_GRASS_TILE_COUNT, FOREST_GRASS_BLEND_COUNT } from "game-common/room/ember/grass_art"
import { forestDirtEdgePosition, groundTileAt, onForestDirtEdgeSoil } from "./dirt_ground"
import { GroundToneSource, toneGroundColors } from "../ground_tone"
import { waterAt } from "../water_presence"
import { buildingShadeColor, buildingShadeGeneration, buildingShadeStrength, contactShadeOf, inBuildingShade, inContactShade, shadeTint } from "../building_shade"

/** Resolve live map edits too: soil or flooring painted over grass must stop the effect. */
export const grassAt = (provider: TileMapMetaProvider, px: number, py: number): boolean => {
    if (!provider) return false
    // The grass under a pond is still grass on map1; the water over it is not.
    if (waterAt(provider, px, py)) return false
    const x = Math.floor(px / TileSize), y = Math.floor(py / TileSize)
    const tile = (layer: string): number => groundTileAt(provider, x, y, layer)
    const overlay = tile("map2")
    if (overlay) {
        const sheet = provider.resolveTileSet(String(overlay))
        const flower = sheet?.tilesetId === "meadow-flowers" || sheet?.tilesetId === FLORA_TILESET &&
            [...FLOWER_PATCH_TILES, ...TRANSPARENT_SMALL_FLOWER_TILES].includes(overlay - sheet.firstGid)
        // A forest-dirt edge is transparent on its grass side: the grass under it is what shows there.
        const dirtEdge = forestDirtEdgePosition(provider, overlay)
        if (!flower && (dirtEdge === undefined || onForestDirtEdgeSoil(dirtEdge, px, py) !== false)) return false
    }
    const gid = tile("map1")
    if (!gid) return false
    const sheet = provider.resolveTileSet(String(gid))
    if (!sheet) return false
    const position = gid - sheet.firstGid
    return (sheet.tilesetId === "forest-grass" && position >= 0 && position < FOREST_GRASS_BLEND_COUNT) ||
        (sheet.tilesetId === "lpc-terrain" && position === CLASSIC_GRASS_TILE) ||
        (sheet.tilesetId === "authored-terrain" && position >= DAPPLED_GRASS_START &&
            position < DAPPLED_GRASS_START + FOREST_GRASS_TILE_COUNT)
}

/**
 * Grass round the feet of bodies (players, NPCs, creatures) is OFF by default for now. Set this to true to turn it on
 * for everyone, or try it live from the console with `window.grassFeet = true` (false turns it back off). Grass round
 * entity tiles -- tree roots (tree_root_fringe), the feet of walls (wall_grass_fringe) -- is its own effect and stays on.
 */
export const GRASS_FEET_ENABLED = false

export const grassFeetEnabled = (): boolean => {
    const live = typeof window === "undefined" ? undefined : (window as any).grassFeet
    return typeof live === "boolean" ? live : GRASS_FEET_ENABLED
}

/** One foot of a generated body, in the world surface's coordinates (Ember bodies bake these per frame). */
export interface GroundFoot {
    /** The ground under the sole. */
    x: number
    y: number
    /** World pixels the sole is above that ground. */
    lift: number
    /** Bearing weight. */
    down: boolean
    /** Its leg hangs on the far side of the body: the body hides it. */
    behind: boolean
}

interface Contact {
    x: number; y: number; scale: number; zIndex: number; alpha: number; moving: boolean; angle?: number; travelAngle?: number
    /** World pixels a jump has the whole body off the ground. */
    lift?: number
    /** The room (storey) the body stands in, whose shade falls on its feet; else the frame's. */
    roomId?: string
    /** The body's own cast shadow as drawn now, which lies on the ground round its feet; none in shade or at night. */
    shadow?: { alpha: number; color: number }
    /** Where each foot really is, for a body that knows. Without them the soles are guessed from the facing. */
    feet?: GroundFoot[]
}
interface Feet { art: Graphics; x: number; y: number; distance: number; side: number; stepMs: number }
/** A tread foot: where it stood, whether there was grass there, and its own tuft while it stands. */
interface Tread {
    down: boolean; grass: boolean; x: number; y: number; landedAt: number; hard: boolean; seed?: number; colors?: number[]; art?: Graphics; key?: string
    /** Which shade lies on the spot (SUN_SHADE | CONTACT_SHADE), as of shade generation `shadeGeneration`. */
    shade?: number; shadeGeneration?: number
}
interface Patch {
    art: Graphics; x: number; y: number; scale: number; lean: number; born: number; alpha: number; bend: number; spread: number
    width?: number; seed?: number; colors?: number[]; key?: string; shade?: number; roomId?: string
}
/** The shade layers drawn over the ground: the sun's (buildings, plants) and the dark at the feet of what stands. */
const SUN_SHADE = 1, CONTACT_SHADE = 2
const RECOVERY_MS = 280
const MAX_PATCHES = 96
/** A body with more feet than this tufts only the nearest (a crab's eight would carpet the ground). */
const MAX_TUFTED_FEET = 4
/** Feet of a many-legged body are smaller than a person's. */
const SMALL_FOOT = 0.8
/** A planted foot dragged this far (a body shoved while standing) looks at the ground again. */
const RECHECK_PX = 3
/** Touching down after this long with no foot on the ground -- a jump's landing, not a run's stride -- flattens a wider spread. */
const LANDING_AIR_MS = 150
const LANDING_SPREAD = 1.6
/** How long a landing presses the blades flat before they stand back up. */
const PRESS_MS = 220
/** A footprint springs back from at least this much press. */
const PATCH_PRESS = 0.7
const TUFT_ALPHA = 0.95
/** Half the fringe's span across a sole seen side-on, in px (it reaches a pixel past each end), and the share a sole seen end-on keeps. */
const TUFT_HALF_WIDTH = 4
const FOOT_WIDTH_FRONT = 0.7
/** The fringe's bottom row, this far in front of (below) the sole. */
const TUFT_ROOT_DROP = 2
/**
 * Every column of the fringe is at least this tall, so it hides the bottom of the shoe -- the outline that makes a
 * body look stood ON the grass rather than in it -- and some columns are a blade poking higher.
 */
const TUFT_DEPTH = 4
/** The share of columns that poke one, and two, px higher, and that fall a px short: the top edge is ragged. */
const TUFT_TALL = 0.4
const TUFT_TALLER = 0.12
const TUFT_SHORT = 0.25
/** The share of a column's pixels a shade lighter or darker than its blade. */
const TUFT_SPECKLE = 0.35
/** The fringe thins to this depth at its very ends, so it has no square corners. */
const TUFT_END_DEPTH = 2
/** Pressed, the fringe loses this share of its height and its ends lean this many px out from the sole. */
const TUFT_PRESS_SQUASH = 0.25
const TUFT_SPLAY = 2.5
/** How far (px) a tall blade may lean either way at rest. */
const TUFT_SWAY = 1.2
/**
 * Standing still, the fringe settles over this long into a tidy one hugging the shoe: as deep (it still hides the
 * bottom of the shoe), but the blades that poke higher keep this share of their extra height, and it spans this
 * share as wide. Walking off, it is back at full size at once.
 */
const STILL_SETTLE_MS = 350
const STILL_POKE = 0
const STILL_SPAN = 0.7
/** Whole-body lift (world px) above which an LPC body, whose feet are not known, is off the ground. */
const AIRBORNE_PX = 1
const POOL_MAX = 48
const BLADE_COLORS = [0x438c39, 0x81b64e, 0x559847, 0x327d3b]
/**
 * The fringe's greens, dark to light: the ember grass's own luminance steps (5th, 20th, 40th, 60th, 80th and 93rd
 * percentiles of forest-grass.png), so grass drawn over a shoe is speckled like the ground it grows from.
 */
export const FRINGE_COLORS = [0x25672c, 0x387c2f, 0x428632, 0x4e9135, 0x629f38, 0x82b53f]

/** Across the stride, compressed vertically for the game's three-quarter view. */
export const grassSoles = (angle: number, scale: number) => {
    const dx = Math.cos(angle), dy = Math.sin(angle)
    const sideView = Math.abs(dx) > Math.abs(dy)
    const acrossX = -dy * 3.5 + (sideView ? dx : 0)
    const acrossY = sideView ? dx * 1.75 : 0
    return [-1, 1].map(side => ({
        x: acrossX * side * scale, y: acrossY * side * scale,
        size: sideView && acrossY * side < 0 ? 0.75 : 1,
    }))
}

/** A hash of a ground spot, so the same spot always grows the same blades. */
const spotSeed = (x: number, y: number): number => {
    let h = Math.imul(Math.round(x), 0x27d4eb2d) ^ Math.imul(Math.round(y), 0x165667b1)
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b)
    return (h ^ (h >>> 13)) | 0
}

/** Which feet get tufts: all of them, or the nearest few of a many-legged body. */
const tuftedFeet = (feet: GroundFoot[]): Set<number> => {
    const order = feet.map((_, i) => i)
    if (feet.length > MAX_TUFTED_FEET) order.sort((a, b) => Number(feet[a].behind) - Number(feet[b].behind) || feet[b].y - feet[a].y)
    return new Set(order.slice(0, MAX_TUFTED_FEET))
}

/**
 * Tiny pixel blades round the feet; a foot that leaves the ground leaves its blades springing back up.
 *
 * A generated (Ember) body hands over its real feet, baked per frame from its skeleton: blades stand round
 * each foot only while it bears weight, pressed flat on touchdown and easing back up; a foot lifting off
 * leaves its blades recovering where it stood. So the grass keeps step with whatever the legs do -- a walk,
 * a run, a strike on the move, a deer's four-beat walk -- and a jump leaves the ground bare until it lands.
 * A body without known feet (an LPC sheet) gets two soles guessed from its facing and steps counted off the
 * distance it covers, as before; off the ground in a jump it shows none.
 *
 * Cheap by construction: the ground is looked at when a foot lands, not every frame; a tuft's shapes are
 * rebuilt only when what it shows changes (moving it is a position); Graphics are pooled.
 * All art lives in the sorted world surface, with no filters, textures, timers, or server events.
 */
export class GrassFeet {
    private feet = new Map<string, Feet>()
    private treads = new Map<string, Tread[]>()
    /** Since when each tread body has had no foot on the ground. */
    private airborneSince = new Map<string, number>()
    /** Since when each tread body has stood still. */
    private stillSince = new Map<string, number>()
    private seen = new Set<string>()
    private patches: Patch[] = []
    private pool: Graphics[] = []
    private roomId?: string
    /** The tint each room's shades give the ground this frame, by "roomId|shade". */
    private tints = new Map<string, number>()

    constructor(private surface: Container, private onGrass: (x: number, y: number) => boolean,
        private groundSource: () => GroundToneSource = () => ({})) {}

    beginFrame(roomId: string) {
        if (this.roomId !== roomId) {
            this.clear()
            this.roomId = roomId
        }
        this.seen.clear()
        this.tints.clear()
    }

    contact(id: string, contact: Contact, now = performance.now()) {
        if (contact.feet?.length) {
            this.feet.get(id) && this.removeGuessed(id)
            this.tread(id, contact, contact.feet, now)
            return
        }
        this.removeTreads(id)
        if ((contact.lift ?? 0) > AIRBORNE_PX) {
            // Off the ground in a jump: no blades round soles that are not there, and no steps in the air.
            this.seen.add(id)
            this.removeGuessed(id)
            return
        }
        this.guessed(id, contact, now)
    }

    private tread(id: string, contact: Contact, feet: GroundFoot[], now: number) {
        const { scale, zIndex, alpha } = contact
        const roomId = contact.roomId ?? this.roomId
        const angle = Number.isFinite(contact.angle) ? contact.angle : Math.PI / 2
        const lean = -Math.cos(Number.isFinite(contact.travelAngle) ? contact.travelAngle : angle)
        // A sole seen side-on is long across the screen, seen from in front or behind it is narrow.
        const width = FOOT_WIDTH_FRONT + (1 - FOOT_WIDTH_FRONT) * Math.abs(Math.cos(angle))
        let treads = this.treads.get(id)
        const fresh = !treads
        if (!treads) this.treads.set(id, treads = [])
        this.seen.add(id)
        const up = !fresh && !treads.some(tread => tread.down)
        if (!up) this.airborneSince.delete(id)
        else if (!this.airborneSince.has(id)) this.airborneSince.set(id, now)
        const landing = up && now - this.airborneSince.get(id) >= LANDING_AIR_MS
        if (contact.moving) this.stillSince.delete(id)
        else if (!this.stillSince.has(id)) this.stillSince.set(id, fresh ? now - STILL_SETTLE_MS : now)
        const still = contact.moving ? 0 : Math.min(1, (now - this.stillSince.get(id)) / STILL_SETTLE_MS)
        const settled = still * still * (3 - 2 * still)
        const tufted = tuftedFeet(feet)
        const size = scale * (feet.length > 2 ? SMALL_FOOT : 1)
        for (let i = treads.length; i > feet.length; i--) this.release(treads.pop())
        feet.forEach((foot, i) => {
            let tread = treads[i]
            if (!tread) {
                // Seen for the first time standing: no footfall, just the blades it is standing in.
                tread = treads[i] = { down: false, grass: false, x: foot.x, y: foot.y, landedAt: now - RECOVERY_MS, hard: false }
                if (foot.down) { tread.down = true; this.lookAtGround(tread, foot) }
            } else if (foot.down && !tread.down) {
                tread.down = true
                tread.landedAt = now
                tread.hard = landing
                this.lookAtGround(tread, foot)
            } else if (foot.down && Math.hypot(foot.x - tread.x, foot.y - tread.y) > RECHECK_PX) {
                this.lookAtGround(tread, foot)
            } else if (!foot.down && tread.down) {
                tread.down = false
                // Lifting off: the blades it stood on spring back up where it stood.
                if (tread.grass && tufted.has(i)) this.stamp(tread, foot.behind ? zIndex - 0.01 : zIndex + 0.01, size, lean, alpha, now, width, roomId)
            }
            const show = tread.down && tread.grass && tufted.has(i)
            if (!show) { if (tread.art) { this.give(tread.art); tread.art = tread.key = undefined } return }
            tread.art ??= this.take()
            tread.art.position.set(Math.round(foot.x), Math.round(foot.y))
            tread.art.zIndex = foot.behind ? zIndex - 0.01 : zIndex + 0.01
            tread.art.alpha = alpha * TUFT_ALPHA
            // Grass drawn over the feet is drawn over the shade layers too: darken it as they darken the ground there.
            if (tread.shadeGeneration !== buildingShadeGeneration()) this.lookAtShade(tread, roomId)
            tread.art.tint = this.underOwnShadow(this.groundTint(roomId, tread.shade), contact.shadow)
            const press = Math.max(0, 1 - (now - tread.landedAt) / PRESS_MS)
            const spread = tread.hard ? 1 + (LANDING_SPREAD - 1) * press : 1
            tread.key = this.drawn(tread.art, tread.key, size, press, lean, spread, tread.colors, width, tread.seed, false, settled)
        })
    }

    private lookAtGround(tread: Tread, foot: GroundFoot) {
        tread.x = foot.x
        tread.y = foot.y
        tread.grass = this.onGrass(foot.x, foot.y)
        tread.colors = tread.grass ? toneGroundColors(this.groundSource(), FRINGE_COLORS, foot.x, foot.y) : undefined
        tread.seed = spotSeed(foot.x, foot.y)
        tread.shadeGeneration = undefined
    }

    private lookAtShade(tread: Tread, roomId: string | undefined) {
        tread.shade = this.shadeAt(roomId, tread.x, tread.y)
        tread.shadeGeneration = buildingShadeGeneration()
    }

    private shadeAt(roomId: string | undefined, x: number, y: number): number {
        return (inBuildingShade(roomId, x, y) ? SUN_SHADE : 0) | (inContactShade(roomId, x, y) ? CONTACT_SHADE : 0)
    }

    /** What the shade layers multiply the ground by where `shade` lies, as hill_layer paints them: white in the sun. */
    private groundTint(roomId: string | undefined, shade = 0): number {
        if (!shade) return 0xffffff
        const key = `${roomId}|${shade}`
        let tint = this.tints.get(key)
        if (tint !== undefined) return tint
        const strength = buildingShadeStrength(roomId) ?? 0
        tint = 0xffffff
        if (shade & SUN_SHADE) tint = shadeTint(tint, strength, buildingShadeColor(roomId))
        const contact = shade & CONTACT_SHADE ? contactShadeOf(roomId) : undefined
        if (contact) tint = shadeTint(tint, Math.min(1, strength * contact.ratio), contact.color)
        this.tints.set(key, tint)
        return tint
    }

    /** `tint` darkened as the body's own shadow darkens the ground round its feet (it is drawn over them as a wash of its colour). */
    private underOwnShadow(tint: number, shadow?: { alpha: number; color: number }): number {
        if (!shadow || shadow.alpha <= 0) return tint
        const own = shadeTint(0xffffff, shadow.alpha, shadow.color)
        const channel = (shift: number) => Math.round(((tint >> shift) & 0xff) * ((own >> shift) & 0xff) / 255) << shift
        return channel(16) | channel(8) | channel(0)
    }

    private stamp(tread: Tread, zIndex: number, scale: number, lean: number, alpha: number, now: number, width: number, roomId?: string) {
        if (this.patches.length >= MAX_PATCHES) return
        const art = this.take()
        art.zIndex = zIndex
        art.position.set(Math.round(tread.x), Math.round(tread.y))
        const press = Math.max(0, 1 - (now - tread.landedAt) / PRESS_MS)
        // Springs back up from however pressed it still was, fading as it rejoins the ground's own grass.
        this.patches.push({ art, x: tread.x, y: tread.y, scale, lean, born: now, alpha, bend: Math.max(PATCH_PRESS, press),
            spread: tread.hard ? 1 + (LANDING_SPREAD - 1) * press : 1, width, seed: tread.seed, colors: tread.colors, shade: tread.shade, roomId })
    }

    /** The old guess, for bodies whose feet are not known: two soles across the facing, a step every few pixels. */
    private guessed(id: string, contact: Contact, now: number) {
        const { x, y, scale, zIndex, alpha, moving } = contact
        const roomId = contact.roomId ?? this.roomId
        const angle = Number.isFinite(contact.angle) ? contact.angle : Math.PI / 2
        const lean = -Math.cos(Number.isFinite(contact.travelAngle) ? contact.travelAngle : angle)
        const soles = grassSoles(angle, scale)
        const left = this.onGrass(x + soles[0].x, y + soles[0].y)
        const right = this.onGrass(x + soles[1].x, y + soles[1].y)
        if (!left && !right) { this.removeGuessed(id); return }
        this.seen.add(id)
        let feet = this.feet.get(id)
        if (!feet) {
            feet = { art: this.take(), x, y, distance: 0, side: -1, stepMs: now - 150 }
            this.feet.set(id, feet)
        }
        const distance = Math.hypot(x - feet.x, y - feet.y)
        // Teleports and re-entry do not stamp a backlog of footsteps.
        if (moving && distance < 32) feet.distance += distance
        else feet.distance = 0
        if (feet.distance >= 9 * scale && now - feet.stepMs >= 90) {
            const side = feet.side
            if ((side < 0 ? left : right) && this.patches.length < MAX_PATCHES) {
                const art = this.take()
                art.zIndex = zIndex - 0.01
                const sole = soles[side < 0 ? 0 : 1]
                const px = x + sole.x, py = y + sole.y
                art.position.set(Math.round(px), Math.round(py))
                this.patches.push({ art, x: px, y: py, scale: scale * sole.size, lean, born: now, alpha, bend: 1, spread: 1,
                    colors: toneGroundColors(this.groundSource(), FRINGE_COLORS, px, py), shade: this.shadeAt(roomId, px, py), roomId })
            }
            feet.side = -side
            feet.distance = 0
            feet.stepMs = now
        }
        feet.x = x; feet.y = y
        feet.art.clear()
        feet.art.position.set(Math.round(x), Math.round(y))
        feet.art.zIndex = zIndex + 0.01
        feet.art.alpha = alpha * 0.85
        feet.art.tint = this.underOwnShadow(this.groundTint(roomId, this.shadeAt(roomId, x, y)), contact.shadow)
        const bend = moving ? Math.max(0, 1 - (now - feet.stepMs) / RECOVERY_MS) : 0
        const tone = (sole: { x: number; y: number }) => toneGroundColors(this.groundSource(), BLADE_COLORS, x + sole.x, y + sole.y)
        if (left) this.blades(feet.art, soles[0].x, soles[0].y, scale * soles[0].size, feet.side === 1 ? bend : 0, lean, 1, tone(soles[0]))
        if (right) this.blades(feet.art, soles[1].x, soles[1].y, scale * soles[1].size, feet.side === -1 ? bend : 0, lean, 1, tone(soles[1]))
    }

    endFrame(now = performance.now()) {
        for (const id of this.feet.keys()) if (!this.seen.has(id)) this.removeGuessed(id)
        for (const id of this.treads.keys()) if (!this.seen.has(id)) this.removeTreads(id)
        this.patches = this.patches.filter(patch => {
            const age = (now - patch.born) / RECOVERY_MS
            if (age >= 1) { this.give(patch.art); return false }
            patch.key = this.drawn(patch.art, patch.key, patch.scale, patch.bend * (1 - age), patch.lean, patch.spread, patch.colors, patch.width, patch.seed, true)
            patch.art.alpha = patch.alpha * TUFT_ALPHA * (1 - age * age)
            patch.art.tint = this.groundTint(patch.roomId, patch.shade)
            return true
        })
    }

    remove(id: string) {
        this.removeGuessed(id)
        this.removeTreads(id)
    }

    clear() {
        for (const id of [...this.feet.keys()]) this.removeGuessed(id)
        for (const id of [...this.treads.keys()]) this.removeTreads(id)
        this.patches.forEach(patch => this.give(patch.art))
        this.patches = []
    }

    private removeGuessed(id: string) { const feet = this.feet.get(id); if (feet) this.give(feet.art); this.feet.delete(id) }
    private removeTreads(id: string) {
        this.treads.get(id)?.forEach(tread => this.release(tread))
        this.treads.delete(id)
        this.airborneSince.delete(id)
        this.stillSince.delete(id)
    }
    private release(tread?: Tread) { if (tread?.art) this.give(tread.art) }

    private take(): Graphics {
        const art = this.pool.pop()
        if (art && !art.destroyed) { art.visible = true; return art }
        const made = new Graphics()
        this.surface.addChild(made)
        return made
    }

    private give(art: Graphics) {
        if (art.destroyed) return
        if (this.pool.length >= POOL_MAX || art.parent !== this.surface) { art.destroy(); return }
        art.clear()
        art.visible = false
        art.tint = 0xffffff
        this.pool.push(art)
    }

    /** Redraw `art` only when the pixels it would show have changed; returns what it now shows. */
    private drawn(art: Graphics, key: string | undefined, scale: number, bend: number, lean: number, spread: number, colors?: number[], width = 1, seed = 0, trail = false, settled = 0): string {
        const next = `${scale}|${Math.round(bend * 12)}|${Math.sign(lean)}|${Math.round(spread * 4)}|${Math.round(width * 8)}|${seed}|${trail}|${Math.round(settled * 8)}|${colors?.join(",")}`
        if (next === key) return key
        art.clear()
        this.tuft(art, scale, bend, lean, spread, colors ?? FRINGE_COLORS, width, seed, trail, settled)
        return next
    }

    /**
     * A fringe of grass across a sole at the art's origin: every column grows deep enough to hide the bottom of the
     * shoe, its top ragged, a few blades poking higher, thinning at the ends. Pressed (`bend` 1, just landed) it sits
     * a little lower and its ends splay away from the sole, standing back up as the foot eases off. Each column is a
     * blade in one of the ground's greens, speckled a step lighter or darker, dark at the root; a poking blade has a
     * light tip. A `trail` (where a foot has just lifted off) is only the blades poking up, springing back.
     */
    private tuft(art: Graphics, scale: number, bend: number, side: number, spread: number, colors: number[], width: number, seed: number, trail = false, settled = 0) {
        const half = Math.max(1, Math.round(TUFT_HALF_WIDTH * width * spread * (1 - (1 - STILL_SPAN) * settled)))
        const px = Math.max(1, Math.round(scale))
        const ground = Math.round(TUFT_ROOT_DROP * scale)
        const last = colors.length - 1
        // Every spot the foot comes down on grows its own fringe.
        let state = seed
        const random = () => {
            state = Math.imul(state ^ (state >>> 15), 0x2c1b3c6d) + 0x9e3779b9 | 0
            return ((state ^ (state >>> 13)) >>> 0) / 4294967296
        }
        for (let c = -half; c <= half; c++) {
            const [tall, sway, tone] = [random(), random(), random()]
            const edge = Math.abs(c) / half
            const end = Math.abs(c) === half
            const grown = tall < TUFT_TALLER ? 2 : tall < TUFT_TALL ? 1 : tall > 1 - TUFT_SHORT ? -1 : 0
            const extra = grown > 0 ? Math.round(grown * (1 - (1 - STILL_POKE) * settled)) : grown
            const depth = end ? TUFT_END_DEPTH : TUFT_DEPTH + extra
            const poke = extra > 0
            if (trail && !poke) continue
            const height = Math.max(1, Math.round(depth * scale * (1 - TUFT_PRESS_SQUASH * bend)))
            const out = Math.sign(c)
            // The ends splay out when pressed; a poking blade leans a little either way, the rest stand.
            const lean = (bend * out * edge * TUFT_SPLAY + (poke ? (sway - 0.5) * 2 * TUFT_SWAY + side * 0.3 : 0)) * scale
            const stem = 1 + Math.floor(tone * 3)
            for (let k = 0; k < height; k++) {
                const u = height > 1 ? k / (height - 1) : 0
                const speck = random()
                const shade = poke && k === height - 1 ? last
                    : k === 0 ? Math.max(0, stem - 1)
                    : Math.min(last - 1, Math.max(0, stem + (speck < TUFT_SPECKLE / 2 ? -1 : speck > 1 - TUFT_SPECKLE / 2 ? 1 : 0)))
                art.beginFill(colors[shade])
                art.drawRect(c * px + Math.round(lean * u * u), ground - k * px, px, px)
                art.endFill()
            }
        }
    }

    private blades(art: Graphics, x: number, y: number, scale: number, bend: number, side: number, spread: number, colors: number[]) {
        // Roots sit slightly in front of the sole, on the ground. Only blade tips cover the shoe.
        const ground = Math.round(y + 2 * scale)
        art.beginFill(colors[3], 0.65)
        art.drawRect(Math.round(x - 3 * scale * spread), ground - 1, Math.max(1, 7 * scale * spread), 1)
        art.endFill()
        for (let i = 0; i < 3; i++) {
            const root = Math.round(x + (i - 1) * 2 * scale * spread)
            const height = Math.max(1, Math.round((i === 1 ? 3 : 2) * scale * (1 - bend * 0.65)))
            // Pressed blades splay away from the foot; otherwise they lean away from the way it travels.
            const away = spread > 1 && i !== 1 ? i - 1 : side
            const lean = Math.round(bend * away * 2 * scale)
            art.beginFill(colors[i])
            art.drawRect(root, ground - 1, Math.max(1, scale), 1)
            art.drawRect(root + lean, ground - height, Math.max(1, scale), height)
            art.endFill()
        }
    }
}
